'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Calendar,
  ChevronRight,
  LogOut,
  Plus,
  User,
  Phone,
  ShieldCheck,
  X
} from 'lucide-react';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import { AppointmentDetailsModal } from './AppointmentDetailsModal';
import { getThemeTokens } from '@/lib/themeTokens';

interface ClientProfilePortalViewProps {
  business: any;
  currentBranch?: any;
  onBackToCatalog: () => void;
  onRescheduleFromProfile: (booking: any) => void;
  initialSelectedBooking?: any; // Para cuando se llega desde "Ver detalles de la cita" en el Drawer
}

export const ClientProfilePortalView: React.FC<ClientProfilePortalViewProps> = ({
  business,
  currentBranch,
  onBackToCatalog,
  onRescheduleFromProfile,
  initialSelectedBooking
}) => {
  // Pestañas: Próximas vs Pasadas (Fiel a Cerini)
  const [tab, setTab] = useState<'proximas' | 'pasadas'>('proximas');

  // Identificación del Cliente (WhatsApp) - Aislado por negocio
  const slug = business?.slug || 'default';
  const bookingsKey = `calenddy_bookings_${slug}`;
  const profileKey = `calenddy_client_profile_${slug}`;

  const [clientPhone, setClientPhone] = useState<string>('');
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState<boolean>(false);
  const [tempPhoneInput, setTempPhoneInput] = useState<string>('');

  // Lista de Reservas
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Modal de Detalles de Cita (Inspección con los 4 iconos superiores)
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<any | null>(null);

  // Cargar reservas sincronizadas desde LocalStorage y PostgreSQL para este comercio
  const loadBookings = useCallback(async (phoneToQuery?: string) => {
    setLoading(true);
    try {
      const stored = localStorage.getItem(bookingsKey);
      let localList: any[] = [];
      if (stored) {
        try {
          localList = JSON.parse(stored);
        } catch (e) {
          console.error(e);
        }
      }

      // Si no es un array válido, inicializar lista vacía (sin inyectar reservas de otros comercios)
      if (!Array.isArray(localList)) {
        localList = [];
      }

      // Sincronizar estados con PostgreSQL si hay IDs de reservas de este comercio
      const validIds = localList.map((b) => b.id).filter(Boolean);
      if (validIds.length > 0) {
        const tokens = localList
          .filter((b) => b.id && (b.accessToken || b.securityCode))
          .map((b) => `${b.id}:${b.accessToken || b.securityCode}`)
          .join(',');

        try {
          const queryParams = new URLSearchParams({
            ids: validIds.join(','),
            ...(tokens ? { tokens } : {}),
            slug: slug
          });
          const res = await fetch(`/api/bookings?${queryParams.toString()}`);
          const data = await res.json();
          if (data.success && Array.isArray(data.appointments)) {
            const merged = localList.map((loc) => {
              const remote = data.appointments.find((r: any) => r.id === loc.id);
              if (remote) {
                return {
                  ...loc,
                  status: remote.status.toUpperCase(),
                  date: remote.dateStr || remote.date || loc.date,
                  dateStr: remote.dateStr || remote.date || loc.dateStr,
                  time: remote.startTime || remote.time || loc.time,
                  startTime: remote.startTime || remote.time || loc.startTime,
                  staffName: remote.staffName || loc.staffName
                };
              }
              return loc;
            });
            setBookings(merged);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Error fetching bookings from server:', e);
        }
      }

      setBookings(localList);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [bookingsKey, slug]);

  useEffect(() => {
    // Cargar perfil guardado exclusivamente para este negocio
    try {
      const storedProfile = localStorage.getItem(profileKey);
      if (storedProfile) {
        const p = JSON.parse(storedProfile);
        if (p.phone) setClientPhone(p.phone);
      } else {
        setClientPhone('');
      }
    } catch (e) {
      console.error(e);
    }

    loadBookings();

    // Si viene una cita preseleccionada (por ejemplo al hacer click en "Ver detalles de la cita" desde el drawer)
    if (initialSelectedBooking) {
      setSelectedBookingForDetails(initialSelectedBooking);
    }
  }, [loadBookings, profileKey, initialSelectedBooking]);

  // Actualizar estado tras cancelar una cita
  const handleBookingCancelled = (cancelledId: string) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === cancelledId ? { ...b, status: 'CANCELLED' } : b))
    );
  };

  // Cerrar sesión del portal en este negocio
  const handleLogout = () => {
    try {
      localStorage.removeItem(profileKey);
    } catch (e) {}
    setClientPhone('');
    setBookings([]);
    setIsPhoneModalOpen(true);
  };

  // Guardar nuevo teléfono identificado para este negocio
  const handleIdentifyPhone = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = tempPhoneInput.trim();
    if (!clean) return;
    try {
      localStorage.setItem(profileKey, JSON.stringify({ phone: clean }));
    } catch (e) {}
    setClientPhone(clean);
    setIsPhoneModalOpen(false);
    loadBookings(clean);
  };

  // Filtrado de citas
  const todayStr = new Date().toISOString().split('T')[0];
  const proximas = bookings.filter((b) => {
    const d = b.dateStr || b.date || '';
    return d >= todayStr || b.status === 'CONFIRMED' || b.status === 'CANCELLED';
  });

  const pasadas = bookings.filter((b) => {
    const d = b.dateStr || b.date || '';
    return d < todayStr && b.status !== 'CONFIRMED' && b.status !== 'CANCELLED';
  });

  const displayedList = tab === 'proximas' ? proximas : pasadas;

  const businessName = business?.name || currentBranch?.name || 'Comercio';
  const brandColor = business?.brandColor || '#111827';
  const theme = getThemeTokens(business?.portalTheme, brandColor);
  const initialLetter = businessName.trim().charAt(0).toUpperCase() || 'C';

  // Jerarquía estricta de Portada:
  // 1. Portada específica de la sucursal actual (ej. Alcorta o Palermo)
  // 2. Imagen de la sucursal actual si no tiene coverUrl específico
  // 3. Portada general del negocio (ej. Cerini, Juan Rojas, Noir)
  // 4. Fallback elegante con el color de marca del negocio (ej. azul de Juan Rojas o gris/negro de Noir)
  const coverImageUrl =
    currentBranch?.coverUrl ||
    currentBranch?.imageUrl ||
    business?.coverUrl ||
    business?.bannerUrl ||
    null;

  // Logo de la marca (siempre consistente en todas las sucursales)
  const brandLogoUrl = business?.logoUrl || business?.imageUrl || null;

  return (
    <div className="min-h-screen flex flex-col font-jakarta bg-white text-[#111827] selection:bg-slate-200">
      
      {/* 1. HEADER SUPERIOR (Fiel a Calendico Cerini: Calendico a la izquierda + Mi perfil y Cerrar sesión a la derecha) */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 sm:px-8 md:px-12 py-3.5">
        <nav className="max-w-[1240px] mx-auto flex items-center justify-between" aria-label="Global">
          {/* Logo Calenddy a la izquierda */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBackToCatalog}
              className="hover:opacity-85 transition cursor-pointer"
            >
              <CalenddyLogo size="sm" variant="dark" />
            </button>
          </div>

          {/* Botones a la derecha: "Mi perfil" oscuro + "Cerrar sesión" */}
          <div className="flex items-center gap-2">
            <div
              style={{
                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                fontSize: '14px',
                fontWeight: 500
              }}
              className="h-10 px-3.5 rounded-xl bg-[#111827] text-white flex items-center gap-2 shadow-xs whitespace-nowrap"
            >
              <User className="w-4 h-4 text-white" />
              <span>Mi perfil</span>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="w-10 h-10 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-700 transition cursor-pointer shadow-2xs shrink-0"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </nav>
      </header>

      {/* 2. Portada (De la sucursal actual, del comercio, o degradado con el brandColor del negocio) */}
      <div className="relative w-full overflow-hidden bg-gray-100">
        {coverImageUrl ? (
          <div className="h-36 sm:h-48 md:h-60 w-full relative">
            <img
              src={coverImageUrl}
              alt={currentBranch?.name ? `${businessName} - ${currentBranch.name}` : businessName}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ) : (
          <div
            style={{
              backgroundColor: brandColor,
              backgroundImage: `linear-gradient(135deg, ${brandColor} 0%, rgba(0, 0, 0, 0.35) 100%)`
            }}
            className="h-36 sm:h-48 md:h-60 w-full relative border-b border-gray-100/10"
          />
        )}
      </div>

      {/* 3. Hoja Blanca Unificada: Superpuesta a la portada con -mt-5 sm:-mt-6 z-10 */}
      <main className="max-w-[1310px] mx-auto w-full relative -mt-5 sm:-mt-6 z-10 bg-white rounded-t-3xl px-4 sm:px-6 lg:px-8 pb-24 flex-1 space-y-8">
        {/* Cabecera de Marca: Logo badge + Nombre + Subtítulo naturalmente centrados en el eje vertical */}
        <div className="pt-4 sm:pt-6 pb-6 border-b border-gray-100">
          <div className="flex items-center space-x-4 sm:space-x-6">
            <div className="shrink-0">
              {brandLogoUrl ? (
                <img
                  src={brandLogoUrl}
                  alt={businessName}
                  className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl object-cover object-center bg-white ring-4 ring-white shadow-sm p-0.5"
                />
              ) : (
                <div
                  style={{
                    backgroundColor: brandColor,
                    color: '#FFFFFF'
                  }}
                  className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl ring-4 ring-white shadow-sm font-bold text-2xl sm:text-4xl flex items-center justify-center select-none"
                >
                  {initialLetter}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 flex flex-col justify-center text-left py-1">
              <h1
                style={{
                  fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  fontSize: '26px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#111827'
                }}
                className="tracking-normal"
              >
                Mi perfil
              </h1>
              <p
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: '22px',
                  color: '#4B5563'
                }}
                className="mt-1 line-clamp-2"
              >
                Tu actividad en {businessName}
                {currentBranch?.name && currentBranch.name !== businessName ? ` · ${currentBranch.name}` : ''} en un solo lugar.
              </p>
            </div>
          </div>
        </div>

        {/* 4. SECCIÓN "TUS CITAS" */}
        <div className="space-y-4 pt-2">
          {/* Título de Sección */}
          <h2
            style={{
              fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontSize: '20px',
              fontWeight: 500,
              lineHeight: '28px',
              color: '#111827'
            }}
          >
            Tus citas
          </h2>

        {/* Pestañas: Próximas (con línea negra inferior) vs Pasadas */}
        <div className="flex items-center space-x-6 border-b border-gray-100 pb-1">
          <button
            type="button"
            onClick={() => setTab('proximas')}
            style={{
              fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
              fontSize: '15px',
              fontWeight: 500
            }}
            className={`pb-3 transition cursor-pointer relative ${
              tab === 'proximas'
                ? 'text-[#111827] border-b-2 border-[#111827]'
                : 'text-gray-400 hover:text-gray-700'
            }`}
          >
            <span>Próximas</span>
            <span className="ml-1.5 text-xs text-gray-400">({proximas.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setTab('pasadas')}
            style={{
              fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
              fontSize: '15px',
              fontWeight: 500
            }}
            className={`pb-3 transition cursor-pointer relative ${
              tab === 'pasadas'
                ? 'text-[#111827] border-b-2 border-[#111827]'
                : 'text-gray-400 hover:text-gray-700'
            }`}
          >
            <span>Pasadas</span>
            <span className="ml-1.5 text-xs text-gray-400">({pasadas.length})</span>
          </button>
        </div>

        {/* Listado de Tarjetas de Cita a Ancho Completo */}
        <div className="space-y-3.5 pt-2">
          {loading ? (
            <div className="space-y-3 py-6">
              <div className="h-20 bg-gray-100 rounded-2xl animate-pulse" />
              <div className="h-20 bg-gray-100 rounded-2xl animate-pulse" />
            </div>
          ) : displayedList.length === 0 ? (
            <div className="text-center py-16 px-4 bg-gray-50/50 rounded-[16px] border border-gray-100">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#111827'
                }}
              >
                No tenés citas {tab === 'proximas' ? 'próximas' : 'pasadas'}
              </p>
              <p
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '13px',
                  color: '#6b7280'
                }}
                className="mt-1 max-w-sm mx-auto"
              >
                Cuando reserves un servicio podrás visualizarlo y autogestionarlo aquí.
              </p>
              <button
                type="button"
                onClick={onBackToCatalog}
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '13px',
                  fontWeight: 500
                }}
                className="mt-5 inline-flex items-center gap-1.5 py-2.5 px-5 rounded-full bg-[#111827] text-white hover:bg-black transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Agendar nueva cita</span>
              </button>
            </div>
          ) : (
            displayedList.map((booking) => {
              const isCancelled = booking.status === 'CANCELLED' || booking.status === 'cancelled';

              // Formato de Mes y Día para el Badge
              let monthShort = 'OCT';
              let dayNum = '16';
              try {
                const parts = (booking.dateStr || booking.date || '').split('-');
                if (parts.length === 3) {
                  const year = parseInt(parts[0]);
                  const month = parseInt(parts[1]) - 1;
                  const day = parseInt(parts[2]);
                  const d = new Date(year, month, day, 12, 0, 0);
                  monthShort = d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '').toUpperCase();
                  dayNum = day.toString();
                }
              } catch (e) {
                console.error(e);
              }

              const startTimeFormatted = booking.startTime || booking.time || '10:00';

              return (
                <div
                  key={booking.id}
                  onClick={() => setSelectedBookingForDetails(booking)}
                  className="flex items-center justify-between p-4 sm:p-5 bg-white rounded-[16px] border border-gray-100 hover:border-gray-200 transition-all cursor-pointer shadow-xs group"
                >
                  {/* LADO IZQUIERDO: BADGE FECHA + INFORMACIÓN (Exacto a Imagen 1) */}
                  <div className="flex items-center space-x-4 min-w-0 flex-1">
                    {/* Badge Lateral Sofisticado con fondo suave rosa/coral (Fiel a Calendico / Cerini) */}
                    <div className="w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 bg-[#fff1f2] border border-[#ffe4e6]/70">
                      <span className="text-[10px] font-bold tracking-wider uppercase leading-none text-[#f43f5e]">
                        {monthShort}
                      </span>
                      <span className="text-lg font-bold leading-tight mt-0.5 text-[#e11d48]">
                        {dayNum}
                      </span>
                    </div>

                    {/* Información de Horario y Servicio */}
                    <div className="min-w-0 flex-1">
                      <p
                        style={{
                          fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}
                      >
                        {startTimeFormatted} AM
                      </p>
                      <h4
                        style={{
                          fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                          fontSize: '16px',
                          fontWeight: 500,
                          lineHeight: '22px'
                        }}
                        className={`line-clamp-2 ${
                          isCancelled
                            ? 'text-gray-400 line-through font-normal'
                            : 'text-[#111827] group-hover:text-black'
                        }`}
                      >
                        {booking.serviceName || 'Corte de hombre'} con {booking.staffName || 'Sebastián'}
                      </h4>
                    </div>
                  </div>

                  {/* LADO DERECHO: CHEVRON ELEGANTE (Idéntico a Imagen 1) */}
                  <div className="flex items-center shrink-0 ml-4">
                    <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      </main>

      {/* 5. MODAL "VER DETALLES DE LA CITA" (Se despliega sobre esta página de Mi Perfil exactamente como en Cerini) */}
      {selectedBookingForDetails && (
        <AppointmentDetailsModal
          isOpen={Boolean(selectedBookingForDetails)}
          onClose={() => setSelectedBookingForDetails(null)}
          booking={selectedBookingForDetails}
          businessName={businessName}
          businessSlug={business?.slug}
          branchName={selectedBookingForDetails.branchName || currentBranch?.name || 'Sede Principal'}
          branchAddress={currentBranch?.address}
          branchPhone={currentBranch?.phone}
          onReschedule={(booking) => {
            setSelectedBookingForDetails(null);
            onRescheduleFromProfile(booking);
          }}
          onCancelled={handleBookingCancelled}
        />
      )}

      {/* 6. MODAL DE LOGIN "¿Cuál es tu WhatsApp?" si se cierra sesión o no está autenticado */}
      {isPhoneModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 sm:p-7 shadow-2xl border border-gray-100 relative animate-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setIsPhoneModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-1">
              ¿Cuál es tu WhatsApp?
            </h3>
            <p className="text-xs text-gray-500 mb-5">
              Ingresá tu número para ver tu historial de citas en {businessName}.
            </p>

            <form onSubmit={handleIdentifyPhone} className="space-y-4">
              <div className="flex items-center rounded-xl border border-gray-200 overflow-hidden focus-within:border-emerald-500">
                <span className="bg-gray-50 px-3 py-2.5 text-xs font-semibold text-gray-600 border-r border-gray-200">
                  🇦🇷 +54
                </span>
                <input
                  type="tel"
                  placeholder="Ej: 11 3176-7545"
                  value={tempPhoneInput}
                  onChange={(e) => setTempPhoneInput(e.target.value)}
                  className="w-full text-xs font-semibold px-3 py-2.5 focus:outline-none"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 px-4 rounded-xl bg-[#1A202C] hover:bg-black text-white text-xs font-semibold transition cursor-pointer shadow-xs"
              >
                Continuar
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer Discreto */}
      <footer className="w-full border-t border-gray-100 py-6 px-4 text-center text-xs mt-auto">
        <div className="max-w-[1240px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-gray-400">
          <p>© {new Date().getFullYear()} {businessName}. Todos los derechos reservados.</p>
          <div className="flex items-center gap-1.5">
            <span>Powered by</span>
            <CalenddyLogo size="xs" variant="dark" />
          </div>
        </div>
      </footer>

      {/* Botón Flotante '+ Volver a agendar' Anclado Abajo a la Derecha (Idéntico a Imagen 1 de Calendico / Cerini) */}
      <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-40">
        <button
          type="button"
          onClick={onBackToCatalog}
          className="inline-flex items-center gap-2 py-3.5 px-6 rounded-full bg-[#111827] hover:bg-black text-white text-sm sm:text-base font-medium transition cursor-pointer shadow-xl active:scale-95"
        >
          <Plus className="w-4 h-4 stroke-[2.5]" />
          <span>Volver a agendar</span>
        </button>
      </div>

    </div>
  );
};
